import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const variants = {
  enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction < 0 ? 300 : -300, opacity: 0 }),
};

export default function TwoReviewsCarousel({ t }) {
  const reviews = [
    {
      name: t("Alexander the Great"),
      avatar: "/avatars/alexander.png",
      text: t(
        "To conquer the world, one must first conquer chaos. With custom schedules and smart rules, my armies marched as if guided by the stars."
      ),
    },
    {
      name: t("Leonidas of Sparta"),
      avatar: "/avatars/leonidas.png",
      text: t(
        "A thousand warriors, three hundred at the Hot Gates. Order and discipline were everything—this tool would have made our stand immortal."
      ),
    },
    {
      name: t("Florence Nightingale"),
      avatar: "/avatars/florence.png",
      text: t(
        "Hospitals thrive on order. This app’s ability to respect availability and days off ensures no nurse is overworked, and no patient left waiting."
      ),
    },
    {
      name: t("Julius Caesar"),
      avatar: "/avatars/caesar.png",
      text: t(
        "I came, I saw, I delegated. Auto-scheduling saved me hours, giving me time to focus on empire—not paperwork."
      ),
    },
    {
      name: t("Leonardo da Vinci"),
      avatar: "/avatars/leonardo.png",
      text: t(
        "Genius lies in design. Unlimited shifts per day and precise custom rules make this a true masterpiece of organization."
      ),
    },
    {
      name: t("Winston Churchill"),
      avatar: "/avatars/churchill.png",
      text: t(
        "In times of great struggle, efficiency is victory. Exporting schedules with ease kept my teams coordinated when every second mattered."
      ),
    },
    {
      name: t("Napoleon Bonaparte"),
      avatar: "/avatars/napoleon.png",
      text: t(
        "Battles are won with strategy, not chaos. This app gives me the precision I demanded of every campaign."
      ),
    },
    // {
    //   name: t("Cleopatra"),
    //   avatar: "/avatars/cleopatra.png",
    //   text: t(
    //     "Ruling an empire requires balance. With smart rules and custom shifts, every detail of my court stayed in harmony."
    //   ),
    // },
    {
      name: t("Nikola Tesla"),
      avatar: "/avatars/tesla.png",
      text: t(
        "Efficiency is the true spark of progress. Auto-scheduling electrifies my workflow like a current flowing without resistance."
      ),
    },
  ];

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [[page, direction], setPage] = useState([0, 0]);
  const timeoutRef = useRef();

  const paginate = useCallback((newDirection) => {
    setPage(([prevPage]) => [prevPage + newDirection, newDirection]);
  }, []);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => paginate(1), 6000);
    return () => clearTimeout(timeoutRef.current);
  }, [page, paginate]);

  const swipePower = (offset, velocity) => Math.abs(offset) * velocity;
  const swipeThreshold = 1000;

  // Determine which reviews to show
  const getVisibleReviews = () => {
    const start = page * (isMdUp ? 2 : 1);
    return Array.from({ length: isMdUp ? 2 : 1 }).map(
      (_, i) => reviews[(start + i) % reviews.length]
    );
  };

  const visibleReviews = getVisibleReviews();

  return (
    <Box maxWidth={900} mx="auto" my={8} textAlign="center">
      <Typography
        variant={isMdUp ? "h3" : "h4"}
        fontWeight={800}
        mb={6}
        sx={{ color: "#ffffff" }}
      >
        {t("Why People Love Us:")}
      </Typography>

      <Box
        position="relative"
        height={isMdUp ? 300 : 280}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        borderRadius="12px"
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "tween", duration: 0.5 },
              opacity: { duration: 0.3 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.25}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeThreshold) paginate(1);
              else if (swipe > swipeThreshold) paginate(-1);
            }}
            style={{
              position: "absolute",
              width: "100%",
              display: "flex",
              gap: 24,
              justifyContent: "center",
            }}
          >
            {visibleReviews.map((review, i) => (
              <Paper
                key={i}
                elevation={1}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  width: isMdUp ? "45%" : "90%",
                }}
              >
                <Avatar
                  src={review.avatar}
                  alt={review.name}
                  sx={{ width: 140, height: 140, mb: 2 }}
                />
                <Typography variant="h6" fontWeight={500}>
                  {review.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                  mt={1}
                >{`“${review.text}”`}</Typography>
              </Paper>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <IconButton
          onClick={() => paginate(-1)}
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => paginate(1)}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
